import {useMutation, useQuery} from '@apollo/client'
import {Field, Form, Formik} from 'formik'
import {Button, Container, Modal, Table} from 'react-bootstrap'
import {GET_PRODUCTS_CREATE_DISPATCH} from '../../graphql/queries/getDespachos'
import {NodeProducts, IProducts} from '../../graphql/types/productsTypes'
import {Spinner} from '../Spinner'
import {useEffect, useState} from 'react'
import {toast} from 'react-toastify'
import {parseId, parsePrice} from '../../helpers'
import {InputCurrency} from '../InputCurrency'

import * as Yup from 'yup'
import {CREATE_PRICE_CLIENT} from '../../graphql/mutations/clientMutations'
const schema = Yup.object({
  prices: Yup.string().required('Ingrese el precio nuevo'),
  productId: Yup.string().required('Debe seleccionar un producto'),
})
interface INodeSelect extends NodeProducts {
  selected: boolean | undefined | null
}
interface ISelectProducts {
  node: INodeSelect
}

interface IRes {
  clientAssingProduct: {
    success: boolean
    errors: {
      message: string
    } | null
  }
}

const initialValues = {
  prices: '',
  productId: '',
}

interface IModalAddPriceProps {
  isOpen: boolean
  onClose: () => void
  infoAddPrice: {name: string; id: string; prevPrices: string[]}
  refetch: () => void
}
export const ModalAddPrice: React.FC<IModalAddPriceProps> = ({
  isOpen,
  onClose,
  infoAddPrice,
  refetch,
}) => {
  const [gqlCreatePrice, {data: dataCreate, loading: loadingCreate, error: errorCreate}] =
    useMutation<IRes>(CREATE_PRICE_CLIENT)
  const {data, loading, error} = useQuery<IProducts>(GET_PRODUCTS_CREATE_DISPATCH, {
    fetchPolicy: 'no-cache',
  })

  const [selectProducts, setSelectProducts] = useState<ISelectProducts[]>([])
  const [allProducts, setAllProducts] = useState<{product: string; value: string}[]>([])

  useEffect(() => {
    if (error) toast.error('Error ' + error.message)
  }, [error])

  useEffect(() => {
    if (errorCreate) toast.error('Error ' + errorCreate.message)
  }, [errorCreate])

  useEffect(() => {
    if (!data) return
    setSelectProducts(data.productList.edges.map(({node}) => ({node: {...node, selected: false}})))
  }, [data])

  useEffect(() => {
    if (!dataCreate) return
    if (dataCreate.clientAssingProduct.success) {
      toast.success('Precio(s) agregado(s) correctamente')
      refetch()
      onClose()
    } else if (dataCreate.clientAssingProduct.errors) {
      toast.error(
        'Error: ' + dataCreate.clientAssingProduct?.errors?.message || 'No se pudo agregar precios'
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataCreate])

  const onSubmit = (
    values: {prices: string; productId: string},
    actions: {resetForm: () => void}
  ) => {
    setAllProducts((prev) => [...prev, {product: values.productId, value: values.prices}])
    setSelectProducts((prev) => {
      const id = values.productId.split('***')[0]
      const newArr = [...prev]
      const find = newArr.find(({node}) => node.id === id)
      if (!find) return newArr
      find.node.selected = true
      return newArr
    })
    actions.resetForm()
  }

  const handleSaveData = () => {
    if (allProducts.length === 0) return
    const input = {
      clientId: parseId(infoAddPrice.id),
      products: allProducts.map(({product, value}) => ({
        productId: parseId(product.split('***')[0]),
        price: value,
      })),
    }
    gqlCreatePrice({
      variables: {input},
    })
  }

  return (
    <Modal show={isOpen} onHide={onClose} size='lg'>
      <Modal.Header closeButton>
        <Modal.Title>
          Agregar precio exclusivo a{' '}
          <span className='fs-2 text-uppercase text-danger'>{infoAddPrice.name}</span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading || !data ? (
          <div
            className='d-flex align-items-center justify-content-center'
            style={{height: '30vh'}}
          >
            <Spinner size='30px' />
          </div>
        ) : (
          <Formik initialValues={initialValues} onSubmit={onSubmit} validationSchema={schema}>
            {(props) => (
              <Form>
                <label className='d-flex align-items-center form-label mb-3'>
                  Productos
                  <i
                    className='fas fa-exclamation-circle ms-2 fs-7'
                    data-bs-toggle='tooltip'
                    title='Seleccione un producto'
                  />
                </label>
                <div className='row mb-2' data-kt-buttons='true'>
                  {selectProducts.map(({node}) => (
                    <div className='col-lg-4 col-sm-6' key={node.id}>
                      <Field
                        type='radio'
                        className='btn-check'
                        name='productId'
                        value={`${node.id}***${node.name}`}
                        id={`kt_product_select_${parseId(node.id)}`}
                        disabled={node.selected || infoAddPrice.prevPrices.includes(node.id)}
                      />
                      <label
                        className='btn btn-outline btn-outline-dashed btn-outline-default w-100 p-4'
                        htmlFor={`kt_product_select_${parseId(node.id)}`}
                        style={{backgroundColor: node.selected ? '#ebebeb' : undefined}}
                      >
                        <span className='fw-bolder fs-3'>{node.name}</span>{' '}
                        {infoAddPrice.prevPrices.includes(node.id) && (
                          <span className='ml-1'>
                            <i className='fs-2 text-success bi bi-check-square' />
                          </span>
                        )}
                        <span className='d-block'>
                          {node.unitType} - {parsePrice(node.unitPrice)}
                        </span>
                      </label>
                    </div>
                  ))}
                </div>
                {props.errors.productId && (
                  <div className='form-text text-danger mb-4'>{props.errors.productId}</div>
                )}
                <div>
                  <label className='fw-bold' htmlFor='prices'>
                    Ingrese el precio
                  </label>
                  <InputCurrency
                    id='prices'
                    placeholder='Ingrese precio'
                    name='prices'
                    onValueChange={(value, name) => props.setFieldValue(name!, value)}
                    onBlur={props.handleBlur}
                    value={props.values.prices}
                  />
                  {props.errors.prices && (
                    <div className='form-text text-danger mb-4'>{props.errors.prices}</div>
                  )}
                </div>
                <div className='my-8'>
                  <Button type='submit' className='w-100' variant='info'>
                    Agregar
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        )}
        {allProducts.length > 0 && (
          <Container>
            <h6 className='my-4 text-center'>Resumen</h6>
            <Table striped bordered={false} hover>
              <thead>
                <tr className='fw-bold text-uppercase'>
                  <th className='text-center'>Producto</th>
                  <th className='text-center'>Nuevo valor</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {allProducts.map((inf) => (
                  <tr key={inf.product}>
                    <td className='text-center'>{inf.product.split('***')[1]}</td>
                    <td className='text-center'>{parsePrice(inf.value)}</td>
                    <td>
                      <button
                        className='m-0 p-0 border-0 bg-transparent text-danger'
                        onClick={() => {
                          setAllProducts((prev) => {
                            const newArr = [...prev]
                            return newArr.filter(({product}) => product !== inf.product)
                          })
                          setSelectProducts((pre) => {
                            const newArr = [...pre]
                            const find = newArr.find(
                              ({node}) => node.id === inf.product.split('***')[0]
                            )
                            if (!find) return newArr
                            find.node.selected = false
                            return newArr
                          })
                        }}
                      >
                        <i className='bi bi-trash' style={{color: 'inherit'}} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Container>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant='primary'
          disabled={allProducts.length === 0 || loadingCreate}
          onClick={handleSaveData}
          className='d-flex align-items-center gap-2'
        >
          {loadingCreate && <Spinner size='16px' color='light' />}
          Guardar
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
